<template>
    <img v-if="psdPreviewUrl" :src="psdPreviewUrl" :alt="id" class="psd-preview" />
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useMutation } from "@vue/apollo-composable";
import type { PSDMasterTemplate } from "shared-types";

import type { PsdComputedOverwrite } from "@/types/types";
import renderPsdTemplateMutation from "@/apollo/mutations/RenderPsdTemplate.gql";
import { BannerEvents } from "@/enums/banners";

const emit = defineEmits(["loading", BannerEvents.BannerError]);

const { id, previewValues } = defineProps<{
    id: string;
    previewValues: string;
}>();

const data = computed(() => {
    return JSON.parse(previewValues) as {
        campaignId: string;
        masterTemplate: PSDMasterTemplate;
        computedOverwrites: PsdComputedOverwrite[];
        psdThumbnail?: string;
        previewUrl?: string;
    };
});

const psdPreviewUrl = ref(null as string | null);

const computedLayers = computed(() => {
    return data.value.computedOverwrites.map(({ value, type, layer }) => ({
        layer,
        type,
        value
    }));
});

const { mutate: renderPsdTemplate } = useMutation(renderPsdTemplateMutation, () => {
    return {
        variables: {
            campaignId: data.value.campaignId,
            masterTemplate: {
                _id: data.value.masterTemplate._id,
                persistentKey: data.value.masterTemplate.persistentKey,
                width: data.value.masterTemplate.width,
                height: data.value.masterTemplate.height,
                backupQuality: data.value.masterTemplate.backupQuality
            },
            computedLayers: computedLayers.value?.length ? computedLayers.value : []
        }
    };
});

async function renderTemplate() {
    if (!computedLayers.value?.length) {
        psdPreviewUrl.value =
            data.value.psdThumbnail || data.value.previewUrl ? `${data.value.previewUrl}/preview.png` : null;

        emit("loading", false);

        return;
    }

    emit("loading", true);

    try {
        const resp = await renderPsdTemplate();

        if (resp) {
            psdPreviewUrl.value = resp.data.renderPsdTemplate;
        } else {
            psdPreviewUrl.value =
                data.value.psdThumbnail || data.value.previewUrl ? `${data.value.previewUrl}/preview.png` : null;
        }
    } catch (error) {
        emit(BannerEvents.BannerError, error);
    }

    emit("loading", false);
}

onMounted(() => {
    // this means new data will only be fetched if the component is re-mounted
    // in the editor, this is controlled via the key prop for the LiveBanner component
    // this is done to so throttle the updates and avoid unnecessary re-renders (with a debounce function)
    // this goes against the Vue reactivity system,
    // there probably is a better way, for example to throttle the apollo mutation here
    renderTemplate();
});
</script>

<style scoped>
.psd-preview {
    width: 100%;
}
</style>
